import { FC } from 'react'
import { Box, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import { useUsersIceContact } from 'services/profile/profile.hooks'
import theme from 'styles/theme'

import { azureButtonSx, profileFieldSx } from './userForm.styles'

interface UserDetailsIceProps {
  onBack: () => void
}

const splitName = (name: string) => {
  const parts = name.trim().split(/\s+/)
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ')
  }
}

const UserDetailsIce: FC<UserDetailsIceProps> = ({ onBack }) => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { usersIceContact, usersIceContactUpdate, usersIceContactCreate, hasIceContact } =
    useUsersIceContact()
  const labelBg = isDesktop ? theme.r4r.greyBg : '#fff'

  const iceForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: [usersIceContact?.firstName, usersIceContact?.lastName].filter(Boolean).join(' '),
      phone: usersIceContact?.phone ?? ''
    },
    onSubmit: async (values) => {
      const payload = {
        ...splitName(values.name),
        phone: values.phone
      }
      if (hasIceContact) {
        await usersIceContactUpdate.mutateAsync(payload)
      } else {
        await usersIceContactCreate.mutateAsync(payload)
      }
    }
  })

  const content = (
    <Box
      component='form'
      onSubmit={iceForm.handleSubmit}
      sx={{ width: '100%', maxWidth: '560px' }}>
      <Typography
        sx={{
          fontSize: '14px',
          color: theme.r4r.ink,
          lineHeight: '24px',
          mb: '32px'
        }}>
        {formatMessage({
          id: 'userDetailsIce.ice',
          defaultMessage:
            'ICE (in case of emergency), to numer do osoby, z którą będziemy kontaktować się w sytuacji awaryjnej, np.: nie mamy z Tobą kontaktu i obawiamy się, że mogło stać się coś niepokojącego.'
        })}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <TextField
          fullWidth
          name='name'
          placeholder={formatMessage({
            id: 'userDetailsIce.form.name',
            defaultMessage: 'Nazwa'
          })}
          value={iceForm.values.name}
          onChange={iceForm.handleChange}
          InputLabelProps={{ shrink: false }}
          sx={profileFieldSx(false, labelBg)}
        />
        <TextField
          fullWidth
          name='phone'
          placeholder={formatMessage({
            id: 'userDetailsIce.form.phone',
            defaultMessage: 'Numer telefonu'
          })}
          value={iceForm.values.phone}
          onChange={iceForm.handleChange}
          InputLabelProps={{ shrink: false }}
          sx={profileFieldSx(false, labelBg)}
        />
      </Box>
      <LoadingButton
        fullWidth
        type='submit'
        loading={iceForm.isSubmitting}
        disabled={iceForm.isSubmitting}
        sx={{ ...azureButtonSx, mt: '32px' }}>
        {formatMessage({
          id: 'userDetailsIce.form.submit',
          defaultMessage: 'Zawnioskuj o zmianę danych'
        })}
      </LoadingButton>
    </Box>
  )

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({
          id: 'userDetailsIce.label',
          defaultMessage: 'Dane kontaktowe'
        })}
        onBack={onBack}
      />
      <BackNavigationContent>{content}</BackNavigationContent>
    </>
  )
}

export default UserDetailsIce
